import type { StorefrontProduct } from '../lib/dropify'
import { getProductCategoryName, getProductPrice } from '../lib/dropify'
import { getProductOrderFacts, isGekkoProduct, isMaskProduct, isMedicalGloveProduct, isNonMedicalMaskProduct } from '../lib/productOrdering'
import { stripHtml, type FaqItem } from '../lib/seo'
import { getProductLandingPilot, type ProductLandingPilot } from './productLandingPilots'
import { STOCKING_APPLE_PATH, STOCKING_APPLE_PRODUCT_SLUG } from './stockingAppleCream'

export type ProductLandingContent = ProductLandingPilot

export function ensureMinimumProductFaq(product: StorefrontProduct, items: FaqItem[]): FaqItem[] {
  if (items.length >= 5) return items
  const name = product.name
  const candidates: FaqItem[] = [
    {
      q: `Bagaimana cara mengecek stok ${name}?`,
      a: `Kirim jumlah dan pilihan ukuran, warna, atau model yang diperlukan. Admin akan memeriksa stok ${name} yang dapat dipenuhi saat permintaan masuk.`,
    },
    {
      q: `Bagaimana harga grosir ${name} ditentukan?`,
      a: `Harga mengikuti pilihan produk, jumlah, dan stok saat penawaran dibuat. Kirim kebutuhan dalam satuan pembelian yang sesuai agar admin dapat menyiapkan quotation.`,
    },
    {
      q: `Apakah ${name} dapat dikirim ke kota saya?`,
      a: `Kirim kota tujuan dan target penerimaan. Admin akan memeriksa pilihan pengiriman, ongkir, dan estimasi yang tersedia untuk pesanan Anda.`,
    },
    {
      q: `Apa yang perlu dikonfirmasi sebelum PO ${name}?`,
      a: `Periksa varian, jumlah, isi kemasan, kota tujuan, serta dokumen yang diperlukan. Admin akan mengonfirmasi rincian tersebut sebelum pesanan diproses.`,
    },
    {
      q: `Apakah sample ${name} dapat diminta?`,
      a: `Tanyakan ketersediaan sample kepada admin. Biaya dan pengiriman sample akan dikonfirmasi untuk produk yang dipilih.`,
    },
  ]
  return [...items, ...candidates.slice(0, 5 - items.length)]
}

type Guidance = Pick<ProductLandingContent, 'fitTitle' | 'fit' | 'checkTitle' | 'checks' | 'detailLabel' | 'detailPlaceholder' | 'messageDetail' | 'messageRequest'>

function guidanceFor(product: StorefrontProduct): Guidance {
  const slug = product.categories[0]?.slug

  if (isMaskProduct(product)) return {
    fitTitle: 'Tipe dan kemasan yang perlu dipilih',
    fit: ['Cocokkan tipe masker dengan kebutuhan penggunaan.', 'Hitung kebutuhan dalam box berdasarkan jumlah pemakai dan frekuensi penggantian.', 'Sebutkan warna dan dokumen yang diwajibkan bila ada.'],
    checkTitle: 'Periksa sebelum PO',
    checks: ['Konfirmasikan isi box untuk tipe yang dipilih.', 'Minta dokumen yang berlaku untuk SKU ini jika ada syarat pengadaan.', 'Periksa stok warna, jumlah box, ongkir, dan target penerimaan bersama admin.'],
    detailLabel: 'Tipe, warna, atau dokumen yang dicari',
    detailPlaceholder: 'Contoh: warna hitam, perlu dokumen AKD',
    messageDetail: 'Tipe, warna, atau dokumen',
    messageRequest: 'Mohon cek isi box, stok, dokumen yang tersedia, dan quotation.',
  }

  if (isMedicalGloveProduct(product)) return {
    fitTitle: 'Ukuran untuk tim Anda',
    fit: ['Tentukan ukuran yang diperlukan untuk setiap pemakai.', 'Tulis jumlah box per ukuran agar stok bisa diperiksa.', 'Sebutkan jenis pekerjaan dan dokumen yang diwajibkan fasilitas Anda.'],
    checkTitle: 'Periksa sebelum PO',
    checks: ['Pastikan bahan, ukuran, serta isi box pada SKU yang dipilih.', 'Minta dokumen yang sesuai dengan seri ini jika pengadaan mensyaratkannya.', 'Konfirmasikan stok per ukuran, harga, dan ongkir.'],
    detailLabel: 'Ukuran dan jumlah per ukuran',
    detailPlaceholder: 'Contoh: M 10 box, L 10 box',
    messageDetail: 'Ukuran dan pembagian jumlah',
    messageRequest: 'Mohon cek stok per ukuran, dokumen yang tersedia, dan quotation.',
  }

  if (isGekkoProduct(product) || slug === 'manshet-sarung-tangan') return {
    fitTitle: 'Kebutuhan pemakaian',
    fit: ['Jelaskan jenis pekerjaan dan kondisi benda yang ditangani.', 'Sebutkan ukuran yang dibutuhkan bila produk memiliki pilihan ukuran.', 'Kirim standar perlindungan yang diwajibkan oleh tim K3 bila ada.'],
    checkTitle: 'Periksa sebelum PO',
    checks: ['Kecocokan untuk risiko sayatan, panas, bahan kimia, atau listrik perlu diperiksa per seri.', 'Pastikan ukuran, isi kemasan, dan jumlah yang diperlukan.', 'Minta foto stok serta dokumen seri yang tersedia sebelum pemesanan.'],
    detailLabel: 'Jenis pekerjaan atau ukuran yang dicari',
    detailPlaceholder: 'Contoh: gudang, ukuran 9',
    messageDetail: 'Pemakaian atau ukuran',
    messageRequest: 'Mohon cek stok, pilihan yang sesuai, dan quotation.',
  }

  if (isNonMedicalMaskProduct(product)) return {
    fitTitle: 'Kebutuhan pemakaian',
    fit: ['Sebutkan penggunaan yang direncanakan.', 'Tentukan jumlah dalam lusin.', 'Tulis warna atau model yang dicari bila ada.'],
    checkTitle: 'Periksa sebelum PO',
    checks: ['Periksa bahan dan bentuk produk sesuai kebutuhan pemakaian.', 'Konfirmasikan warna, isi kemasan, dan stok.', 'Kirim kota tujuan untuk pengecekan ongkir.'],
    detailLabel: 'Pemakaian atau warna yang dicari',
    detailPlaceholder: 'Contoh: untuk toko, warna hitam',
    messageDetail: 'Pemakaian atau warna',
    messageRequest: 'Mohon cek pilihan, stok, dan quotation.',
  }

  if (slug === 'bra-lingerie' || slug === 'cd-underwear' || /\bbra\b/i.test(product.name)) return {
    fitTitle: 'Pilihan untuk stok toko',
    fit: ['Tentukan nomor model dan jumlah yang akan dipesan.', 'Tulis pembagian ukuran yang dicari.', 'Sebutkan pilihan warna agar admin dapat memeriksa stoknya.'],
    checkTitle: 'Periksa sebelum PO',
    checks: ['Kombinasi ukuran dan warna mengikuti model serta stok.', 'Minta foto barang jika perlu memeriksa potongan, bahan, atau jahitan.', 'Konfirmasikan isi kemasan, harga, dan ongkir sebelum PO.'],
    detailLabel: 'Ukuran, warna, atau model yang dicari',
    detailPlaceholder: 'Contoh: ukuran 36 dan 38, hitam',
    messageDetail: 'Ukuran, warna, atau model',
    messageRequest: 'Mohon cek stok pilihan ukuran dan warna serta quotation.',
  }

  if (slug === 'kaos-kaki') return {
    fitTitle: 'Pilihan untuk stok toko',
    fit: ['Tentukan model dan jumlah yang diperlukan.', 'Sebutkan ukuran pemakai yang dituju.', 'Kirim pilihan warna atau motif bila ada.'],
    checkTitle: 'Periksa sebelum PO',
    checks: ['Pastikan isi per lusin dan pilihan ukuran untuk model ini.', 'Konfirmasikan stok warna atau motif sebelum menyusun pesanan.', 'Kirim kota tujuan untuk mengecek ongkir.'],
    detailLabel: 'Ukuran, warna, atau motif yang dicari',
    detailPlaceholder: 'Contoh: dewasa, hitam',
    messageDetail: 'Ukuran, warna, atau motif',
    messageRequest: 'Mohon cek isi kemasan, pilihan yang tersedia, dan quotation.',
  }

  return {
    fitTitle: 'Data yang membantu pemilihan',
    fit: ['Sebutkan model atau kode produk yang dicari.', 'Tulis ukuran, warna, atau varian yang diperlukan bila ada.', 'Kirim perkiraan jumlah dan kota tujuan.'],
    checkTitle: 'Periksa sebelum PO',
    checks: ['Konfirmasikan bahan, ukuran, serta isi kemasan yang berlaku untuk produk ini.', 'Periksa stok pilihan yang akan dipesan.', 'Minta harga dan ongkir sesuai jumlah serta kota tujuan.'],
    detailLabel: 'Varian atau kebutuhan penggunaan',
    detailPlaceholder: 'Contoh: ukuran, warna, atau model',
    messageDetail: 'Varian atau penggunaan',
    messageRequest: 'Mohon cek spesifikasi, stok, satuan pembelian, dan quotation.',
  }
}

export function getProductLandingContent(product: StorefrontProduct, related: StorefrontProduct[], faq: FaqItem[]): ProductLandingContent {
  const pilot = getProductLandingPilot(product)
  if (pilot) return { ...pilot, faq: ensureMinimumProductFaq(product, pilot.faq) }

  const category = product.sku === 'TALLY-BH-3070' ? 'Bra & Lingerie' : getProductCategoryName(product)
  const parentHref = product.sku === 'TALLY-BH-3070' ? '/bra-tally' : product.categories[0] ? `/kategori/${product.categories[0].slug}` : '/katalog'
  const parentLabel = product.sku === 'TALLY-BH-3070' ? 'Bra Tally grosir' : product.categories[0]?.name ?? 'Katalog produk'
  const order = getProductOrderFacts(product)
  const guidance = guidanceFor(product)
  const description = stripHtml(product.description).replace(/\s*—\s*/g, '. ').replace(/\s+/g, ' ').trim()
  const variantNames = product.variants.map((variant) => variant.name?.trim() || variant.sku).filter(Boolean)
  const minMatch = order?.minimumLabel.match(/(?:mulai )?(\d+) (lusin|box)/i)
  const facts = [
    ...(product.sku ? [{ label: 'Kode produk', value: product.sku }] : []),
    { label: 'Minimum order', value: minMatch ? `${minMatch[1]} ${minMatch[2]}` : 'Konfirmasi admin' },
    ...(order?.orderUnit ? [{ label: 'Satuan', value: order.orderUnit }] : []),
    ...(order?.packLabel ? [{ label: 'Isi kemasan', value: order.packLabel }] : []),
    ...(variantNames.length > 1 ? [{ label: 'Pilihan', value: `${variantNames.length} varian` }] : []),
  ]
  const alternatives: ProductLandingContent['alternatives'] = related.slice(0, 2).map((item) => ({
    label: item.name,
    href: `/produk/${item.slug || item.id}`,
    description: 'Lihat model dan ketentuan pembeliannya.',
    itemId: item.id,
    itemCategory: getProductCategoryName(item),
    price: getProductPrice(item),
  }))
  if (product.slug === STOCKING_APPLE_PRODUCT_SLUG) alternatives.unshift({
    label: 'Panduan grosir Stocking Apple B08B cream',
    href: STOCKING_APPLE_PATH,
    description: 'Lihat detail model dan cara meminta penawaran.',
  })

  return {
    slug: product.slug,
    id: product.id,
    category,
    parentHref,
    parentLabel,
    eyebrow: `Grosir ${category.toLowerCase()}`,
    summary: description || `${product.name}. Konfirmasikan spesifikasi, varian, dan isi kemasan dengan admin sebelum memesan.`,
    buyingNote: order ? `${order.minimumLabel}${order.packLabel ? ` ${order.packLabel}` : ''}` : 'Minimum order dan satuan pembelian dikonfirmasi admin sebelum quotation.',
    facts,
    ...guidance,
    unit: order?.orderUnit ?? null,
    quantityPlaceholder: order?.orderUnit ? `Isi jumlah ${order.orderUnit}` : 'Isi perkiraan jumlah',
    alternatives: alternatives.length ? alternatives : [{ label: `Lihat ${parentLabel}`, href: parentHref, description: 'Bandingkan produk lain sebelum memilih.' }],
    faq: ensureMinimumProductFaq(product, faq),
  }
}
